import { useCallback, useRef, useState } from "react";
import { surveyApi } from "@/lib/apiClient";
import { DIALOGUE_END_PHRASE } from "@shared-domain/survey/voice";
import { ASR_AUDIO_CONSTRAINTS } from "./mic";

/** 完了フレーズ検知から自動終了までの猶予 (AI が音声を言い終えるのを待つ。Unizo と同じ 5s)。 */
const AUTO_END_DELAY_MS = 5000;

/** stop() 時に未確定の文字起こしを待つ上限 (最後の一言の取りこぼし防止。Unizo PD-511 と同じ 3s)。 */
const PENDING_TRANSCRIPT_WAIT_MS = 3000;

/**
 * 発話直後に停止された場合、VAD (silence_duration_ms=500) が commit するまで待つ猶予。
 * commit 前は pending が 0 のままなので、これが無いと最後の一言が捨てられる。
 */
const COMMIT_GRACE_MS = 1000;

/**
 * UniQ 音声モードの軽量 OpenAI Realtime クライアント (PRODUCT_DEV-574 MVP)。
 * Unizo の useRealtimeConnection の中核フロー (session→PC→mic→DataChannel→SDP) を踏襲した
 * 最小実装。ユーザー発話の文字起こしを蓄積し、停止時に返す。
 *
 * ⚠ iOS 音量制御・自動再接続・セッションタイマー等の堅牢化 (Unizo 相当) は未実装。
 *   実 OpenAI 接続を伴うため、デプロイ後の実機検証が必要 (本ファイルは検証前提の初版)。
 */
export type VoiceStatus = "idle" | "connecting" | "listening" | "error";
/** 対話モードのチャット表示用ターン (AI=assistant / ユーザー=user)。 */
export interface VoiceTurn {
  role: "assistant" | "user";
  text: string;
  done: boolean;
}

/** 対話モードで session.update により注入するセッション設定 (instructions/tools/音声入力)。 */
export interface RealtimeSessionConfig {
  instructions?: string;
  tools?: Record<string, unknown>[];
  /** 文字起こし/VAD/ノイズ抑制 (buildAudioInputConfig の戻り値)。mint 済み設定の再適用。 */
  audioInput?: Record<string, unknown>;
}
/** ツール実行結果。output は function_call_output としてモデルへ返す JSON 文字列。 */
export interface ToolCallResult {
  output: string;
  /** 次の response.create に載せる追い instructions (例:「remaining の先頭だけ質問」)。 */
  followUpInstructions?: string;
}

export function useRealtimeVoice(opts?: {
  /** 対話モード: AI が完了フレーズを発話したら猶予後に呼ばれる (全設問回答済みの自動終了)。 */
  onAutoEnd?: () => void;
  /** function calling: モデルのツール呼び出しを同期実行し結果を返す (record_answer 等)。 */
  onToolCall?: (name: string, args: Record<string, unknown>) => ToolCallResult;
}) {
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [transcript, setTranscript] = useState("");
  const [turns, setTurns] = useState<VoiceTurn[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const transcriptRef = useRef("");
  const turnsRef = useRef<VoiceTurn[]>([]);
  // 起動世代。stop()/再start で ++ し、await 復帰後に自世代でなければ生成物を破棄する
  // (StrictMode 二重呼び出し・接続中の離脱でマイク/PC がリークするのを防ぐ)。
  const genRef = useRef(0);
  // 自動終了: onAutoEnd は毎レンダー最新を参照し、発火は1接続につき1回。
  const onAutoEndRef = useRef(opts?.onAutoEnd);
  onAutoEndRef.current = opts?.onAutoEnd;
  const onToolCallRef = useRef(opts?.onToolCall);
  onToolCallRef.current = opts?.onToolCall;
  const autoEndFiredRef = useRef(false);
  const autoEndTimerRef = useRef<number | null>(null);
  // 未確定の文字起こし数 (commit 済みだが transcription.completed 未着)。
  // stop() 時にこれが 0 になるまで短時間待ち、最後の一言の取りこぼしを防ぐ (Unizo PD-511 と同じ狙い)。
  const pendingTranscriptsRef = useRef(0);
  // 発話検知中 (speech_started 済み・commit 未着)。停止直後に commit を待つ判断に使う。
  const speakingRef = useRef(false);

  const teardown = useCallback(() => {
    if (autoEndTimerRef.current !== null) {
      clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = null;
    }
    dcRef.current?.close();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current?.close();
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current.srcObject = null;
      audioElRef.current.remove();
    }
    dcRef.current = null;
    streamRef.current = null;
    pcRef.current = null;
    audioElRef.current = null;
  }, []);

  const start = useCallback(
    async (
      surveyId: string,
      mode: "freerec" | "dialog",
      sessionConfig?: RealtimeSessionConfig,
    ) => {
      setError(null);
      setTranscript("");
      transcriptRef.current = "";
      turnsRef.current = [];
      setTurns([]);
      setPaused(false);
      autoEndFiredRef.current = false;
      pendingTranscriptsRef.current = 0; // 前セッションの取り残しを持ち込まない
      speakingRef.current = false;
      setStatus("connecting");
      const myGen = ++genRef.current;
      const cancelled = () => myGen !== genRef.current;
      try {
        const session = await surveyApi.realtimeSession(surveyId, mode);
        if (cancelled()) return;
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
        });
        pcRef.current = pc;
        // 対話モードの AI 音声受信用
        pc.addTransceiver("audio", { direction: "recvonly" });
        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        audioEl.style.display = "none";
        document.body.appendChild(audioEl);
        audioElRef.current = audioEl;
        pc.ontrack = (e) => {
          audioEl.srcObject = e.streams[0] ?? null;
          void audioEl.play().catch(() => {});
        };

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: ASR_AUDIO_CONSTRAINTS,
        });
        if (cancelled()) {
          stream.getTracks().forEach((t) => t.stop());
          pc.close();
          audioEl.remove();
          return;
        }
        streamRef.current = stream;
        stream.getTracks().forEach((t) => pc.addTrack(t, stream));

        const dc = pc.createDataChannel("oai-events");
        dcRef.current = dc;
        dc.onopen = () => {
          setStatus("listening");
          try {
            // instructions/tools を session.update で注入 (Unizo Recording/index.tsx と同形。
            // mint 時にサーバが焼き込んだ instructions はこれで上書きされる)。
            if (
              sessionConfig &&
              (sessionConfig.instructions || sessionConfig.tools || sessionConfig.audioInput)
            ) {
              dc.send(JSON.stringify({
                type: "session.update",
                session: {
                  type: "realtime",
                  ...(sessionConfig.instructions ? { instructions: sessionConfig.instructions } : {}),
                  ...(sessionConfig.tools ? { tools: sessionConfig.tools, tool_choice: "auto" } : {}),
                  // 音声入力 (文字起こし/VAD/ノイズ抑制) は mint 済みだが、Unizo と同じく
                  // 接続後にも再適用して取りこぼしを無くす。
                  ...(sessionConfig.audioInput
                    ? { audio: { input: sessionConfig.audioInput } }
                    : {}),
                },
              }));
            }
            // 対話モードは AI から先に話し出す (開始挨拶 + 最初の設問)。自由モードは待ち受けのみ。
            if (mode === "dialog") dc.send(JSON.stringify({ type: "response.create" }));
          } catch { /* ignore */ }
        };
        dc.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            // function calling: ツール呼び出しを同期実行し function_call_output を返して次の応答を促す
            // (Unizo useRealtimeMessageHandler/useAsyncToolExecutor と同形。エラーでも output を
            //  返してダングリングさせない)。
            if (
              msg.type === "response.output_item.done" &&
              msg.item?.type === "function_call" &&
              typeof msg.item.name === "string"
            ) {
              let output = JSON.stringify({ error: "tool execution failed" });
              let followUp: string | undefined;
              try {
                const args =
                  typeof msg.item.arguments === "string"
                    ? (JSON.parse(msg.item.arguments) as Record<string, unknown>)
                    : ((msg.item.arguments ?? {}) as Record<string, unknown>);
                const r = onToolCallRef.current?.(msg.item.name, args);
                if (r) {
                  output = r.output;
                  followUp = r.followUpInstructions;
                }
              } catch { /* output は error のまま返す */ }
              try {
                dc.send(JSON.stringify({
                  type: "conversation.item.create",
                  item: { type: "function_call_output", call_id: msg.item.call_id, output },
                }));
                dc.send(JSON.stringify({
                  type: "response.create",
                  ...(followUp ? { response: { instructions: followUp } } : {}),
                }));
              } catch { /* ignore */ }
            }
            // VAD が発話を検知 → commit 待ちに入る (停止直後の grace 判定に使う)。
            if (msg.type === "input_audio_buffer.speech_started") {
              speakingRef.current = true;
            }
            // 発話が commit されたら文字起こし待ちを 1 件計上する。
            if (msg.type === "input_audio_buffer.committed") {
              speakingRef.current = false;
              pendingTranscriptsRef.current += 1;
            }
            // completed/failed は transcript の中身に関わらず必ず待ちを解放する
            // (空 transcript の completed が来ても pending がリークしないように)。
            if (
              msg.type === "conversation.item.input_audio_transcription.completed" ||
              msg.type === "conversation.item.input_audio_transcription.failed"
            ) {
              pendingTranscriptsRef.current = Math.max(0, pendingTranscriptsRef.current - 1);
            }
            // ユーザー発話の文字起こし確定を蓄積 (+ 対話チャットのユーザーターン)
            if (
              msg.type === "conversation.item.input_audio_transcription.completed" &&
              typeof msg.transcript === "string"
            ) {
              transcriptRef.current = `${transcriptRef.current} ${msg.transcript}`.trim();
              setTranscript(transcriptRef.current);
              // 直前の未確定 assistant ターンを確定してからユーザーターンを積む
              // (delta と次の応答が同一バブルに混ざるのを防ぐ)。
              const prev = turnsRef.current;
              const lastA = prev[prev.length - 1];
              const base = lastA && lastA.role === "assistant" && !lastA.done
                ? [...prev.slice(0, -1), { ...lastA, done: true }]
                : prev;
              turnsRef.current = [...base, { role: "user", text: msg.transcript, done: true }];
              setTurns(turnsRef.current);
            }
            // AI(assistant)の音声トランスクリプト: delta で逐次追記、done で確定 (対話チャット)。
            // GA Realtime API のイベント名は response.output_audio_transcript.* / response.output_text.*
            // (Unizo useRealtimeMessageHandler と同じ)。旧 beta 名 response.audio_transcript.* も併置。
            if (
              (msg.type === "response.output_audio_transcript.delta" ||
                msg.type === "response.output_text.delta" ||
                msg.type === "response.audio_transcript.delta") &&
              typeof msg.delta === "string"
            ) {
              const t = turnsRef.current;
              const last = t[t.length - 1];
              if (last && last.role === "assistant" && !last.done) {
                turnsRef.current = [...t.slice(0, -1), { ...last, text: last.text + msg.delta }];
              } else {
                turnsRef.current = [...t, { role: "assistant", text: msg.delta, done: false }];
              }
              setTurns(turnsRef.current);
            }
            if (
              msg.type === "response.output_audio_transcript.done" ||
              msg.type === "response.output_text.done" ||
              msg.type === "response.audio_transcript.done"
            ) {
              const t = turnsRef.current;
              const last = t[t.length - 1];
              if (last && last.role === "assistant" && !last.done) {
                const text =
                  typeof msg.transcript === "string"
                    ? msg.transcript
                    : typeof msg.text === "string"
                      ? msg.text
                      : last.text;
                turnsRef.current = [...t.slice(0, -1), { ...last, text, done: true }];
                setTurns(turnsRef.current);
                // 全設問完了: AI が完了フレーズを言ったら、言い終える猶予の後に自動終了する。
                if (text.includes(DIALOGUE_END_PHRASE) && !autoEndFiredRef.current) {
                  autoEndFiredRef.current = true;
                  autoEndTimerRef.current = window.setTimeout(() => {
                    autoEndTimerRef.current = null;
                    onAutoEndRef.current?.();
                  }, AUTO_END_DELAY_MS);
                }
              }
            }
          } catch {
            /* ignore non-JSON */
          }
        };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const sdpResp = await fetch(
          `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(session.model)}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${session.clientSecret.value}`,
              "Content-Type": "application/sdp",
              Accept: "application/sdp",
            },
            body: offer.sdp,
          },
        );
        if (cancelled()) { teardown(); return; }
        if (!sdpResp.ok) throw new Error(`WebRTC 接続に失敗しました: ${await sdpResp.text()}`);
        await pc.setRemoteDescription({ type: "answer", sdp: await sdpResp.text() });
        if (cancelled()) { teardown(); return; }
      } catch (e) {
        setError(String(e));
        setStatus("error");
        teardown();
      }
    },
    [teardown],
  );

  /**
   * 停止して蓄積した文字起こしを返す。in-flight な start も無効化する。
   * `waitPending` 指定時のみ、未確定の文字起こしを最大 PENDING_TRANSCRIPT_WAIT_MS 待ってから
   * 切断し、最後の一言が抽出から漏れるのを防ぐ (抽出へ進む「終了して確認」でだけ待つ。
   * 離脱/アンマウントでは待たずに即マイクを解放する)。
   */
  const stop = useCallback(async (waitPending = false): Promise<string> => {
    if (waitPending) {
      // ①話し終えた直後の停止では VAD の commit がまだ来ていない (pending=0) ため、
      //   commit を待つ猶予を置く。②その後、確定 (completed/failed) まで drain する。
      const graceDeadline = Date.now() + COMMIT_GRACE_MS;
      while (
        speakingRef.current &&
        pendingTranscriptsRef.current === 0 &&
        Date.now() < graceDeadline
      ) {
        await new Promise((r) => setTimeout(r, 100));
      }
      const deadline = Date.now() + PENDING_TRANSCRIPT_WAIT_MS;
      while (pendingTranscriptsRef.current > 0 && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 100));
      }
    }
    speakingRef.current = false;
    pendingTranscriptsRef.current = 0;
    genRef.current++; // in-flight start をキャンセル
    teardown();
    setStatus("idle");
    setPaused(false);
    return transcriptRef.current;
  }, [teardown]);

  /** 一時停止/再開: マイク送信トラックを有効/無効に切り替える (接続は維持)。 */
  const pause = useCallback(() => {
    streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = false; });
    setPaused(true);
  }, []);
  const resume = useCallback(() => {
    streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = true; });
    setPaused(false);
  }, []);

  /** 最新の対話ターン (stop() の待ち中に届いた分も含む。state はクロージャで古くなるため)。 */
  const getTurns = useCallback((): VoiceTurn[] => turnsRef.current, []);

  return { status, transcript, turns, error, paused, start, stop, pause, resume, getTurns };
}
